/**
 * ⚠️ HAND-WRITTEN FILE - DO NOT DELETE! ⚠️
 *
 * This is the main blog landing page/index that lists all blog posts.
 * It includes search, filtering, and display functionality.
 *
 * File Structure:
 * - blog/index.tsx (THIS FILE) - Hand-written blog landing page
 * - blog/posts/*.tsx - Auto-generated blog post pages (from markdown-routes plugin)
 *
 * Source Files:
 * - Blog posts source: content/blog/ (all .md files)
 * - Plugin: vite-plugins/markdown-routes.ts
 *
 * ⚠️ DO NOT confuse this with auto-generated files in blog/posts/
 */

import { createFileRoute, Link, useNavigate, Outlet, useMatches } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { trpc } from '@/lib/trpc';
import { createPageMeta, PAGE_KEYWORDS } from '@/lib/meta';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import CardActions from '@mui/material/CardActions';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Collapse from '@mui/material/Collapse';
import CircularProgress from '@mui/material/CircularProgress';
import SearchIcon from '@mui/icons-material/Search';
import DateIcon from '@mui/icons-material/CalendarMonth';
import ReadTimeIcon from '@mui/icons-material/Schedule';
import GridViewIcon from '@mui/icons-material/ViewModule';
import ListViewIcon from '@mui/icons-material/ViewList';
import FilterIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';
import PersonIcon from '@mui/icons-material/Person';
import StatsIcon from '@mui/icons-material/QueryStats';
import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import dayjs from 'dayjs';
import { pageColors, getGradient } from '@/theme/colors';
import { useThemeContext } from '@/contexts/ThemeContext';
import { analytics } from '@/lib/analytics';


function BlogPending() {
  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            border: '3px solid',
            borderColor: 'divider',
            borderTopColor: 'primary.main',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            '@keyframes spin': { to: { transform: 'rotate(360deg)' } },
          }}
        />
      </Box>
    </Container>
  );
}

export const Route = createFileRoute('/blog')({
  component: BlogIndexPage,
  pendingComponent: BlogPending,
  pendingMs: 150,
  loader: async ({ context, location }) => {
    if (location.pathname.startsWith('/blog/posts/')) return;
    const defaultInput = {
      q: '',
      tags: [] as string[],
      tagMode: 'any' as const,
      year: '',
      author: '',
      sort: 'date-desc' as const,
      readTime: 'all' as const,
    };
    try {
      await Promise.all([
        context.queryClient.ensureQueryData(trpc.blog.list.queryOptions(defaultInput)),
        context.queryClient.ensureQueryData(trpc.blog.tags.queryOptions(undefined, { staleTime: 60_000 })),
        context.queryClient.ensureQueryData(trpc.blog.facets.queryOptions(undefined, { staleTime: 60_000 })),
      ]);
    } catch {
      // DB unavailable — still render the index/posts rather than blocking SSR
    }
  },
  head: ({ matches }) => {
    if (matches.some(match => match.id.startsWith('/blog/posts/'))) return {};
    const base = createPageMeta({
      title: 'Blog - Christian Battaglia',
      description: 'Browse all blog posts about technology, projects, and more.',
      path: '/blog',
      image: '/og/blog.png',
      imageAlt: 'Blog — Christian Battaglia',
      keywords: PAGE_KEYWORDS.blog,
    });
    return {
      ...base,
      scripts: [
        {
          type: 'application/ld+json',
          children: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Blog',
            name: 'Christian Battaglia — Blog',
            url: 'https://cdbattaglia.com/blog',
            description: 'Writing about infrastructure, product engineering, music technology, and building software.',
            author: { '@type': 'Person', '@id': 'https://cdbattaglia.com/#person', name: 'Christian Battaglia' },
            keywords: PAGE_KEYWORDS.blog.join(', '),
            inLanguage: 'en-US',
          }),
        },
      ],
    };
  },
  validateSearch: (search: Record<string, unknown>): BlogSearch => ({
    q: (search.q as string) || undefined,
    tags: (search.tags as string) || undefined,
    year: (search.year as string) || undefined,
    author: (search.author as string) || undefined,
    sort: (search.sort as string) || undefined,
    view: (search.view as string) || undefined,
    readTime: (search.readTime as string) || undefined,
  }),
});

interface BlogSearch {
  q?: string;
  tags?: string;
  year?: string;
  author?: string;
  sort?: string;
  view?: string;
  readTime?: string;
}

interface BlogPost {
  slug: string;
  title: string;
  author?: string;
  authorImage?: string;
  headerImage?: string;
  description?: string;
  date?: string;
  readTime?: string;
  tags?: string[];
  url: string;
}

type SortOption = 'date-desc' | 'date-asc' | 'title-asc' | 'title-desc' | 'read-asc' | 'read-desc';
type ViewMode = 'grid' | 'list';
type ReadTimeFilter = 'all' | 'quick' | 'medium' | 'long';
type TagMode = 'any' | 'all';

// ⚡ Performance: Server-computed values (no client-side computation needed!)
interface EnhancedBlogPost extends BlogPost {
  dateTimestamp: number;
  readTimeMinutes: number;
  formattedDateShort: string;
  formattedDateLong: string;
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

const JOURNEY_UNITS = ['yr', 'mo', 'd', 'hr', 'min', 'sec'] as const;

function WritingJourneyClock({ since }: { since: string }) {
  const valueRefs = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    const start = dayjs(since);
    const pad = (n: number) => (n < 10 ? `0${n}` : String(n));

    function tick() {
      const now = dayjs();
      const y = now.diff(start, 'year');
      const afterYears = start.add(y, 'year');
      const mo = now.diff(afterYears, 'month');
      const afterMonths = afterYears.add(mo, 'month');
      const d = now.diff(afterMonths, 'day');
      const afterDays = afterMonths.add(d, 'day');
      const h = now.diff(afterDays, 'hour');
      const afterHours = afterDays.add(h, 'hour');
      const m = now.diff(afterHours, 'minute');
      const afterMinutes = afterHours.add(m, 'minute');
      const s = now.diff(afterMinutes, 'second');

      const vals = [String(y), String(mo), String(d), pad(h), pad(m), pad(s)];
      vals.forEach((v, i) => {
        const el = valueRefs.current[i];
        if (el && el.textContent !== v) el.textContent = v;
      });
    }

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [since]);

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.5 }}>
      {JOURNEY_UNITS.map((label, i) => (
        <Box key={label} sx={{ textAlign: 'center' }}>
          <Typography
            ref={(el: HTMLElement | null) => { valueRefs.current[i] = el; }}
            variant="h6"
            sx={{
              color: 'white',
              fontWeight: 700,
              fontVariantNumeric: 'tabular-nums',
              lineHeight: 1.2,
            }}
          >
            0
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(255,255,255,0.6)',
              fontSize: '0.6rem',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            {label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function BlogIndexPage() {
  const { mode } = useThemeContext();
  const matches = useMatches();
  const navigate = useNavigate();
  const searchParams = Route.useSearch();

  // --- Local UI state (initialized from URL params) ---
  const [searchInput, setSearchInput] = useState(searchParams.q || '');
  const [selectedTags, setSelectedTags] = useState<string[]>(
    searchParams.tags ? searchParams.tags.split(',').filter(Boolean) : [],
  );
  const [selectedYear, setSelectedYear] = useState(searchParams.year || 'all');
  const [selectedAuthor, setSelectedAuthor] = useState(searchParams.author || 'all');
  const [sortBy, setSortBy] = useState<SortOption>((searchParams.sort as SortOption) || 'date-desc');
  const [viewMode, setViewMode] = useState<ViewMode>((searchParams.view as ViewMode) || 'grid');
  const [readTimeFilter, setReadTimeFilter] = useState<ReadTimeFilter>(
    (searchParams.readTime as ReadTimeFilter) || 'all',
  );
  const [tagMode, setTagMode] = useState<TagMode>('any');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showStats, setShowStats] = useState(false);

  // Debounce the text search so we don't fire a server query per keystroke
  const debouncedSearch = useDebouncedValue(searchInput, 300);

  // --- Build the query input from current filter state ---
  const queryInput = useMemo(() => ({
    q: debouncedSearch || '',
    tags: selectedTags.length > 0 ? selectedTags : [],
    tagMode: (selectedTags.length > 1 ? tagMode : 'any') as 'any' | 'all',
    year: selectedYear !== 'all' ? selectedYear : '',
    author: selectedAuthor !== 'all' ? selectedAuthor : '',
    sort: sortBy as 'date-desc' | 'date-asc' | 'title-asc' | 'title-desc' | 'read-asc' | 'read-desc',
    readTime: readTimeFilter as 'all' | 'quick' | 'medium' | 'long',
  }), [debouncedSearch, selectedTags, tagMode, selectedYear, selectedAuthor, sortBy, readTimeFilter]);

  // --- Server queries ---
  const { data: listData, isFetching } = useQuery({
    ...trpc.blog.list.queryOptions(queryInput),
    placeholderData: keepPreviousData,
  });
  const { data: tagsData } = useQuery(trpc.blog.tags.queryOptions(undefined, { staleTime: 60_000 }));
  const { data: facetsData } = useQuery(trpc.blog.facets.queryOptions(undefined, { staleTime: 60_000 }));

  const posts = (listData?.posts ?? []) as EnhancedBlogPost[];
  const totalFiltered = listData?.totalFiltered ?? 0;
  const allTags = tagsData ?? [];
  const years = facetsData?.years ?? [];
  const yearCounts = facetsData?.yearCounts ?? {};
  const authors = facetsData?.authors ?? [];
  const totalPosts = facetsData?.totalPosts ?? 0;
  const earliestDate = facetsData?.earliestDate ?? null;

  // --- URL sync (debounced via debouncedSearch, instant for other filters) ---
  const isInitialized = useRef(false);
  useEffect(() => {
    if (!isInitialized.current) {
      isInitialized.current = true;
      return;
    }
    const params: Record<string, string> = {};
    if (debouncedSearch) params.q = debouncedSearch;
    if (selectedTags.length > 0) params.tags = selectedTags.join(',');
    if (selectedYear !== 'all') params.year = selectedYear;
    if (selectedAuthor !== 'all') params.author = selectedAuthor;
    if (sortBy !== 'date-desc') params.sort = sortBy;
    if (viewMode !== 'grid') params.view = viewMode;
    if (readTimeFilter !== 'all') params.readTime = readTimeFilter;
    navigate({ search: params as any, replace: true, resetScroll: false });
  }, [debouncedSearch, selectedTags, selectedYear, selectedAuthor, sortBy, viewMode, readTimeFilter, navigate]);

  // --- Analytics ---
  const analyticsTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!debouncedSearch) return;
    if (analyticsTimer.current) clearTimeout(analyticsTimer.current);
    analyticsTimer.current = setTimeout(() => {
      analytics.searchExecuted(debouncedSearch, 'blog', totalFiltered);
    }, 300);
    return () => { if (analyticsTimer.current) clearTimeout(analyticsTimer.current); };
  }, [debouncedSearch, totalFiltered]);

  // --- Pagination (client-side on server-filtered results) ---
  const POSTS_PER_PAGE = 12;
  const [visibleCount, setVisibleCount] = useState(POSTS_PER_PAGE);

  useEffect(() => {
    setVisibleCount(POSTS_PER_PAGE);
  }, [queryInput]);

  const visiblePosts = useMemo(
    () => posts.slice(0, visibleCount),
    [posts, visibleCount],
  );
  const hasMore = visibleCount < posts.length;

  // --- Active filter count ---
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (searchInput) count++;
    if (selectedTags.length > 0) count++;
    if (selectedYear !== 'all') count++;
    if (selectedAuthor !== 'all') count++;
    if (readTimeFilter !== 'all') count++;
    return count;
  }, [searchInput, selectedTags, selectedYear, selectedAuthor, readTimeFilter]);

  const clearAllFilters = useCallback(() => {
    setSearchInput('');
    setSelectedTags([]);
    setSelectedYear('all');
    setSelectedAuthor('all');
    setReadTimeFilter('all');
    setSortBy('date-desc');
    analytics.filtersCleared('blog');
  }, []);

  const toggleTag = useCallback((tag: string) => {
    setSelectedTags(prev => {
      const next = prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag];
      analytics.filterApplied('blog', 'tag', tag);
      return next;
    });
  }, []);

  const tagCloudMaxCount = useMemo(() => {
    return Math.max(...allTags.map(t => t.count), 1);
  }, [allTags]);

  // Check if we're on a child route (e.g., /blog/posts/*)
  // If so, render the child route instead of the blog index
  const isChildRoute = matches.some(match => match.id.startsWith('/blog/posts/'));
  if (isChildRoute) {
    return <Outlet />;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Hero */}
      <Box
        sx={{
          py: { xs: 6, md: 10 },
          background: getGradient(pageColors.blog, mode),
          color: 'white',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            {/* Left: Name & Stats */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Stack spacing={3} alignItems={{ xs: 'center', md: 'flex-start' }} textAlign={{ xs: 'center', md: 'left' }}>
                {/* Name */}
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 400,
                      opacity: 0.8,
                      letterSpacing: '0.5px',
                    }}
                  >
                    blog of
                  </Typography>
                  <Typography
                    variant="h4"
                    component="h1"
                    sx={{
                      fontWeight: 900,
                      lineHeight: 1.2,
                    }}
                  >
                    Christian Battaglia
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9, mt: 0.5 }}>
                    @cdbattags
                  </Typography>
                </Box>

                {/* Blog Stats Card */}
                <Card
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.15)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    width: '100%',
                    boxShadow: 'none',
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Stack spacing={1.5}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: 'white',
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          letterSpacing: '1px',
                          fontSize: '0.75rem',
                          mb: 0.5,
                        }}
                      >
                        Blog Stats
                      </Typography>
                      <Box>
                        <Typography variant="h5" fontWeight="bold" suppressHydrationWarning sx={{ color: 'white' }}>
                          {totalPosts} articles
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                          Published thoughts
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="h5" fontWeight="bold" suppressHydrationWarning sx={{ color: 'white' }}>
                          {allTags.length} tags
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                          Topics covered
                        </Typography>
                      </Box>
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: 'white',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            fontSize: '0.75rem',
                            mb: 0.5,
                          }}
                        >
                          Writing journey
                        </Typography>
                        {earliestDate ? (
                          <WritingJourneyClock since={earliestDate} />
                        ) : (
                          <Typography variant="h5" fontWeight="bold" sx={{ color: 'white' }}>
                            {years.length} years
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Stack>
            </Grid>

            {/* Right: Description */}
            <Grid size={{ xs: 12, md: 8 }}>
              <Stack spacing={3}>
                <Typography variant="h5" sx={{ opacity: 0.95, fontWeight: 600 }}>
                  Thoughts on Technology & Building
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: '1.1rem',
                    lineHeight: 1.8,
                    opacity: 0.95,
                  }}
                >
                  Exploring software engineering, system architecture, product thinking, and the craft of building things.
                  From technical deep-dives to product philosophy, these are lessons learned from the trenches.
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: '1.1rem',
                    lineHeight: 1.8,
                    opacity: 0.95,
                  }}
                >
                  Browse by topic, search for specific insights, or explore the full chronological archive below.
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 8 }}>
        {/* Search and Controls */}
        <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
          <Stack spacing={3}>
            {/* Search Bar */}
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search articles by title, description, tags, or author..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (searchInput || isFetching) && (
                  <InputAdornment position="end">
                    {isFetching && <CircularProgress size={18} sx={{ mr: 1 }} />}
                    {searchInput && (
                      <IconButton size="small" onClick={() => setSearchInput('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
            />

            {/* Quick Filters Row */}
            <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center" sx={{ gap: 2 }}>
              {/* View Mode */}
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, val) => { if (val) { setViewMode(val); analytics.viewModeChanged(val); } }}
                size="small"
              >
                <ToggleButton value="grid">
                  <GridViewIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="list">
                  <ListViewIcon fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>

              {/* Sort */}
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => { const v = e.target.value as SortOption; setSortBy(v); analytics.filterApplied('blog', 'sort', v); }}
                >
                  <MenuItem value="date-desc">Newest First</MenuItem>
                  <MenuItem value="date-asc">Oldest First</MenuItem>
                  <MenuItem value="title-asc">Title (A-Z)</MenuItem>
                  <MenuItem value="title-desc">Title (Z-A)</MenuItem>
                  <MenuItem value="read-asc">Shortest First</MenuItem>
                  <MenuItem value="read-desc">Longest First</MenuItem>
                </Select>
              </FormControl>

              {/* Advanced Filters Toggle */}
              <Badge badgeContent={activeFilterCount} color="primary">
                <Button
                  variant={showAdvanced ? 'contained' : 'outlined'}
                  startIcon={<FilterIcon />}
                  onClick={() => setShowAdvanced(!showAdvanced)}
                >
                  Filters
                </Button>
              </Badge>

              {/* Stats Toggle */}
              <Button
                variant={showStats ? 'contained' : 'outlined'}
                startIcon={<StatsIcon />}
                onClick={() => setShowStats(!showStats)}
              >
                Stats
              </Button>

              {/* Clear All */}
              {activeFilterCount > 0 && (
                <Button
                  variant="text"
                  color="error"
                  startIcon={<ClearIcon />}
                  onClick={clearAllFilters}
                >
                  Clear All
                </Button>
              )}
            </Stack>

            {/* Advanced Filters Panel */}
            <Collapse in={showAdvanced}>
              <Paper variant="outlined" sx={{ p: 3, mt: 2 }}>
                <Stack spacing={3}>
                  <Typography variant="h6" gutterBottom>
                    Advanced Filters
                  </Typography>

                  {/* Year Filter */}
                  {years.length > 0 && (
                    <FormControl fullWidth size="small">
                      <InputLabel>Year</InputLabel>
                      <Select
                        value={selectedYear}
                        label="Year"
                        onChange={(e) => { setSelectedYear(e.target.value); analytics.filterApplied('blog', 'year', e.target.value); }}
                      >
                        <MenuItem value="all">All Years</MenuItem>
                        {years.map(year => (
                          <MenuItem key={year} value={year}>
                            {year} ({yearCounts[year] ?? 0} posts)
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {/* Author Filter */}
                  {authors.length > 0 && (
                    <FormControl fullWidth size="small">
                      <InputLabel>Author</InputLabel>
                      <Select
                        value={selectedAuthor}
                        label="Author"
                        onChange={(e) => { setSelectedAuthor(e.target.value); analytics.filterApplied('blog', 'author', e.target.value); }}
                      >
                        <MenuItem value="all">All Authors</MenuItem>
                        {authors.map(author => (
                          <MenuItem key={author} value={author}>{author}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {/* Read Time Filter */}
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Reading Time
                    </Typography>
                    <ToggleButtonGroup
                      value={readTimeFilter}
                      exclusive
                      onChange={(_, val) => { if (val) { setReadTimeFilter(val); analytics.filterApplied('blog', 'read_time', val); } }}
                      size="small"
                      fullWidth
                    >
                      <ToggleButton value="all">All</ToggleButton>
                      <ToggleButton value="quick">Quick (&lt;5 min)</ToggleButton>
                      <ToggleButton value="medium">Medium (5-15 min)</ToggleButton>
                      <ToggleButton value="long">Long (15+ min)</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>

                  {/* Tag Mode */}
                  {selectedTags.length > 1 && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Tag Match Mode
                      </Typography>
                      <ToggleButtonGroup
                        value={tagMode}
                        exclusive
                        onChange={(_, val) => val && setTagMode(val)}
                        size="small"
                        fullWidth
                      >
                        <ToggleButton value="any">Match ANY tag</ToggleButton>
                        <ToggleButton value="all">Match ALL tags</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                  )}
                </Stack>
              </Paper>
            </Collapse>

            {/* Stats Panel */}
            <Collapse in={showStats}>
              <Paper variant="outlined" sx={{ p: 3, mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Blog Statistics
                </Typography>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Posts by Year
                    </Typography>
                    <Stack spacing={1}>
                      {years.map(year => (
                        <Stack key={year} direction="row" justifyContent="space-between">
                          <Typography variant="body2">{year}</Typography>
                          <Chip label={yearCounts[year] ?? 0} size="small" />
                        </Stack>
                      ))}
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Top Tags
                    </Typography>
                    <Stack spacing={1}>
                      {allTags.slice(0, 10).map(({ tag, count }) => (
                        <Stack key={tag} direction="row" justifyContent="space-between">
                          <Typography variant="body2">{tag}</Typography>
                          <Chip label={count} size="small" />
                        </Stack>
                      ))}
                    </Stack>
                  </Grid>
                </Grid>
              </Paper>
            </Collapse>

            {/* Active Filters Display */}
            {activeFilterCount > 0 && (
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
                  Active filters:
                </Typography>
                {searchInput && (
                  <Chip
                    label={`Search: "${searchInput}"`}
                    onDelete={() => setSearchInput('')}
                    size="small"
                  />
                )}
                {selectedTags.map(tag => (
                  <Chip
                    key={tag}
                    label={`Tag: ${tag}`}
                    onDelete={() => toggleTag(tag)}
                    size="small"
                    color="primary"
                  />
                ))}
                {selectedYear !== 'all' && (
                  <Chip
                    label={`Year: ${selectedYear}`}
                    onDelete={() => setSelectedYear('all')}
                    size="small"
                  />
                )}
                {selectedAuthor !== 'all' && (
                  <Chip
                    label={`Author: ${selectedAuthor}`}
                    onDelete={() => setSelectedAuthor('all')}
                    size="small"
                  />
                )}
                {readTimeFilter !== 'all' && (
                  <Chip
                    label={`Read: ${readTimeFilter}`}
                    onDelete={() => setReadTimeFilter('all')}
                    size="small"
                  />
                )}
              </Stack>
            )}
          </Stack>
        </Paper>

        {/* Tag Cloud */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
            Popular Tags (click to filter):
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
            {allTags.slice(0, 30).map(({ tag, count }) => {
              const isSelected = selectedTags.includes(tag);
              const fontSize = 0.75 + (count / tagCloudMaxCount) * 0.5;

              return (
                <Chip
                  key={tag}
                  label={`${tag} (${count})`}
                  onClick={() => toggleTag(tag)}
                  color={isSelected ? 'primary' : 'default'}
                  variant={isSelected ? 'filled' : 'outlined'}
                  size="small"
                  sx={{
                    fontSize: `${fontSize}rem`,
                    cursor: 'pointer',
                  }}
                />
              );
            })}
          </Stack>
        </Box>

        {/* Results Count */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" suppressHydrationWarning>
            Showing {visiblePosts.length} of {totalFiltered} articles
            {totalFiltered !== totalPosts && ` (${totalPosts} total)`}
          </Typography>
        </Stack>

        {/* Posts Display */}
        {posts.length > 0 ? (
          viewMode === 'grid' ? (
            <Grid container spacing={4}>
              {visiblePosts.map((post) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={post.slug}>
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 4,
                      },
                    }}
                  >
                    {post.headerImage && (
                      <CardMedia
                        component="img"
                        height="200"
                        image={post.headerImage}
                        alt={post.title}
                        sx={{ objectFit: 'cover' }}
                      />
                    )}
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Stack spacing={2}>
                        {post.tags && post.tags.length > 0 && (
                          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 0.5 }}>
                            {post.tags.slice(0, 5).map((tag, idx) => (
                              <Chip
                                key={idx}
                                label={tag}
                                size="small"
                                onClick={(e) => {
                                  e.preventDefault();
                                  toggleTag(tag);
                                }}
                              />
                            ))}
                          </Stack>
                        )}
                        <Typography variant="h6" component="h2" fontWeight="bold">
                          {post.title}
                        </Typography>
                        {post.description && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                            }}
                          >
                            {post.description}
                          </Typography>
                        )}
                        <Stack direction="row" spacing={2} sx={{ fontSize: '0.875rem' }}>
                          {post.formattedDateShort && (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <DateIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {post.formattedDateShort}
                              </Typography>
                            </Stack>
                          )}
                          {post.readTime && (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <ReadTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {post.readTime}
                              </Typography>
                            </Stack>
                          )}
                        </Stack>
                      </Stack>
                    </CardContent>
                    <CardActions>
                      <Button
                        component={Link}
                        to={post.url}
                        size="large"
                        fullWidth
                        onClick={() => analytics.contentClicked('blog_post', post.title, post.url)}
                      >
                        Read Article
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Stack spacing={3}>
              {visiblePosts.map((post) => (
                <Card
                  key={post.slug}
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    transition: 'box-shadow 0.2s',
                    '&:hover': {
                      boxShadow: 4,
                    },
                  }}
                >
                  {post.headerImage && (
                    <CardMedia
                      component="img"
                      sx={{ width: { xs: '100%', sm: 200 }, height: { xs: 200, sm: 'auto' } }}
                      image={post.headerImage}
                      alt={post.title}
                    />
                  )}
                  <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <CardContent sx={{ flex: 1 }}>
                      <Stack spacing={2}>
                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 0.5 }}>
                          {post.tags?.slice(0, 5).map((tag, idx) => (
                            <Chip
                              key={idx}
                              label={tag}
                              size="small"
                              onClick={() => toggleTag(tag)}
                            />
                          ))}
                        </Stack>
                        <Typography variant="h5" component="h2" fontWeight="bold">
                          {post.title}
                        </Typography>
                        {post.description && (
                          <Typography variant="body2" color="text.secondary">
                            {post.description}
                          </Typography>
                        )}
                        <Stack direction="row" spacing={3}>
                          {post.formattedDateLong && (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <DateIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {post.formattedDateLong}
                              </Typography>
                            </Stack>
                          )}
                          {post.readTime && (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <ReadTimeIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {post.readTime}
                              </Typography>
                            </Stack>
                          )}
                          {post.author && (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary">
                                {post.author}
                              </Typography>
                            </Stack>
                          )}
                        </Stack>
                      </Stack>
                    </CardContent>
                    <CardActions>
                      <Button
                        component={Link}
                        to={post.url}
                        size="large"
                        onClick={() => analytics.contentClicked('blog_post', post.title, post.url)}
                      >
                        Read Article
                      </Button>
                    </CardActions>
                  </Box>
                </Card>
              ))}
            </Stack>
          )
        ) : null}

        {hasMore && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Button
              variant="outlined"
              size="large"
              onClick={() => {
                setVisibleCount((c) => c + POSTS_PER_PAGE);
                analytics.loadMoreClicked('blog', visibleCount + POSTS_PER_PAGE);
              }}
            >
              Load More ({totalFiltered - visibleCount} remaining)
            </Button>
          </Box>
        )}

        {posts.length === 0 && !isFetching && (
          <Paper sx={{ p: 8, textAlign: 'center' }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No articles found matching your filters
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Try adjusting your search criteria or clearing some filters
            </Typography>
            <Button
              variant="contained"
              onClick={clearAllFilters}
              startIcon={<ClearIcon />}
            >
              Clear All Filters
            </Button>
          </Paper>
        )}
      </Container>
    </Box>
  );
}
